"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  type ComponentPropsWithoutRef,
  type ComponentRef,
  forwardRef,
  type ReactNode,
  useEffect,
} from "react";

import { CloseIcon } from "@/components/ui/primitives/icons";
import { cn } from "@/lib/utils";

import { Button } from "./button";
import { IconButton } from "./icon-button";

/* ── Primitive parts ──
 * modal={false}로 Radix의 react-remove-scroll를 비활성화.
 * react-remove-scroll가 모든 스크롤 가능 요소를 잠그면서
 * layout의 내부 스크롤 컨테이너의 scrollTop을 리셋하는 문제 방지.
 * overlay는 커스텀 div로 구현하여 modal={false}에서도 표시되도록 함.
 * 배경 스크롤 잠금은 DialogContent의 라이프사이클에 맞춰 body overflow를 수동 제어.
 */

// DialogPortal 내부에 두어 open=true 일 때만 마운트되도록 한다.
// - mount(= open) 에 body position:fixed + top:-scrollY + overflow:hidden 으로 배경 스크롤 동결
// - unmount(= close) 에 원래 inline 스타일 복구 + 원래 scrollY 로 되돌림
// - 여러 다이얼로그가 겹쳐도 LIFO 복원으로 최상단 원래 값까지 정확히 복귀
const BodyScrollLock = () => {
  useEffect(() => {
    const scrollY = window.scrollY;
    const { body } = document;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      overflow: body.style.overflow,
    };
    Object.assign(body.style, {
      position: "fixed",
      top: `-${scrollY}px`,
      left: "0",
      right: "0",
      overflow: "hidden",
    });
    return () => {
      Object.assign(body.style, prev);
      window.scrollTo(0, scrollY);
    };
  }, []);
  return null;
};

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;
const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("fixed inset-0 z-[1400] bg-black/40", className)}
      {...props}
    />
  )
);
DialogOverlay.displayName = "DialogOverlay";

const DialogContent = forwardRef<
  ComponentRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    width?: string;
    onOverlayClick?: () => void;
  }
>(({ className, width = "652px", children, style, onOverlayClick, ...props }, ref) => {
  return (
    <DialogPortal>
      <BodyScrollLock />
      <DialogOverlay onClick={onOverlayClick} />
      <DialogPrimitive.Content
        ref={ref}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={cn(
          "fixed left-1/2 top-1/2 z-[1400] -translate-x-1/2 -translate-y-1/2",
          "w-full h-auto",
          "rounded-lg border border-gray-500",
          "bg-white p-5",
          "shadow-[0px_5px_5px_0px_rgba(191,191,191,0.15)]",
          "focus:outline-none",
          className
        )}
        style={{ maxWidth: width, minWidth: width, ...style }}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = "DialogContent";

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex items-center justify-between", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogTitle = forwardRef<
  ComponentRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-[18px] font-[700] leading-[140%] text-[#212A4A]", className)}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

const DialogDescription = forwardRef<
  ComponentRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(
      "text-[15px] font-[500] leading-[160%] text-gray-900 whitespace-pre-wrap",
      className
    )}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex justify-end gap-3 mt-auto", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

/* ── Composite: ConfirmModal ── */

interface ConfirmModalProps {
  title: string;
  closeBtn?: string | ReactNode;
  isOpen: boolean;
  onClose: () => void;
  confirmBtn: string;
  content?: string | ReactNode;
  onConfirm: () => void;
}

const ConfirmModal = ({
  title,
  closeBtn,
  confirmBtn,
  content,
  isOpen,
  onClose,
  onConfirm,
}: ConfirmModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()} modal={false}>
      <DialogContent width="652px" onOverlayClick={onClose}>
        <div className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogClose asChild>
              <IconButton
                variant="primary"
                size="small"
                aria-label="닫기"
                icon={<CloseIcon size={24} className="text-gray-800" />}
              />
            </DialogClose>
          </DialogHeader>

          <div className="text-[15px] font-[500] leading-[160%] text-gray-900 whitespace-pre-wrap mt-2 mb-2">
            {typeof content === "string" ? <p>{content}</p> : content}
          </div>

          <DialogFooter>
            {closeBtn &&
              (typeof closeBtn === "string" ? (
                <Button variant="outlinedSecondary" size="large" onClick={onClose}>
                  {closeBtn}
                </Button>
              ) : (
                closeBtn
              ))}
            <Button
              variant="primary"
              size="large"
              onClick={() => {
                onConfirm();
                onClose();
              }}
            >
              {confirmBtn}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ── Composite: SuccessModal ── */

interface SuccessModalProps {
  title: string;
  isOpen: boolean;
  confirmBtn: string;
  content?: string;
  onConfirm: () => void;
  onClose: () => void;
}

const SuccessModal = ({
  title,
  confirmBtn,
  content,
  isOpen,
  onConfirm,
  onClose,
}: SuccessModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()} modal={false}>
      <DialogContent width="652px" onOverlayClick={onClose}>
        <div className="flex flex-col gap-4">
          <DialogHeader className="py-[7px]">
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          {content && (
            <p className="text-[15px] font-[400] leading-[160%] text-gray-900 whitespace-pre-wrap mt-2 mb-2">
              {content}
            </p>
          )}

          <DialogFooter>
            <Button variant="primary" size="large" onClick={onConfirm}>
              {confirmBtn}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export {
  ConfirmModal,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  SuccessModal,
};

export type { ConfirmModalProps, SuccessModalProps };
